import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessageCircle, X, Send, Bot, User, Loader2,
  ChevronDown, PanelRight, Minimize2, LogOut, LogIn,
} from 'lucide-react';
import { COMPANY } from '../../config/company';
import { useAuth } from '../../context/AuthContext';
import LoginModal from '../auth/LoginModal';

const SYSTEM_PROMPT = `Tu es un expert Auth0 senior chez Auth0.Expert. Tu as 10+ ans d'expérience sur Auth0/Okta et tu as accompagné des centaines de migrations IAM en production.

PHILOSOPHIE : Donne d'abord une réponse technique concrète et actionnable AVANT de parler de nos services. Le client doit repartir avec quelque chose d'utile même s'il ne nous contacte pas. C'est comme ça qu'on gagne sa confiance.

COMPORTEMENT :
1. RÉPONDRE directement à la question posée — avec du code si pertinent, des chiffres réels, des pièges concrets.
2. ILLUSTRER avec des exemples tirés de vrais projets ("on a vu ça chez un SaaS B2B à 200K users...").
3. ALLER en profondeur : ne pas rester en surface. Si la question est sur le PKCE, expliquer pourquoi c'est important, quand l'utiliser, et le code de base.
4. SEULEMENT APRÈS avoir apporté de la valeur : mentionner brièvement que Auth0.Expert peut accélérer ou sécuriser la mise en oeuvre si le projet est complexe. Une phrase max, naturelle, pas agressive.
5. NE JAMAIS commencer par pitcher les services. NE JAMAIS refuser de répondre pour "orienter vers un RDV".

EXPERTISE COUVERTE : OAuth 2.0 / OIDC / SAML, Actions & Hooks, Organizations multi-tenant, MFA / Passkeys / WebAuthn, RBAC / FGA, M2M / API security, migration depuis Keycloak / Cognito / LDAP / Firebase, Universal Login customization, Rate limits & scalabilité, RGPD / conformité, coûts & plans Auth0, intégrations (Stripe, Azure AD, Salesforce, Next.js, React Native...).

TON : Expert mais accessible. Franc. Pas de langue de bois. Pas de "excellente question !". Réponds en français, avec des blocs de code markdown quand c'est utile.

STRUCTURE DES RÉPONSES (adapter selon complexité) :
- Réponse directe (1-3 phrases)
- Explication + code ou exemple si pertinent
- Point de vigilance / piège courant (optionnel)
- [Si cas complexe] : "Sur un projet de cette envergure, on peut vous aider à éviter les erreurs classiques — contact@auth0.expert"`;

const WELCOME_MSG = {
  id: 'welcome',
  role: 'assistant',
  content: 'Bonjour 👋 Je suis votre expert Auth0 — posez-moi n\'importe quelle question technique ou stratégique sur Auth0 : architecture, migration, sécurité, coûts, intégrations. Je vous donne une réponse directe et concrète.',
  ts: new Date(),
};

const SUGGESTIONS = [
  'Comment implémenter le PKCE ?',
  'Auth0 vs Keycloak en 2025 ?',
  'MFA obligatoire sur une route ?',
  'Migrer 500K users sans downtime ?',
];

const SIDEBAR_DEFAULT = 420;
const SIDEBAR_MIN     = 300;
const SIDEBAR_MAX     = 800;
const FREE_LIMIT      = 10;
const CHAT_STORAGE    = 'auth0expert_chat_v2';

// ── Helpers ───────────────────────────────────────────────────────────────────
function cleanContent(raw) {
  return raw
    .replace(/【[^】]*】/g, '')
    .replace(/\[\[([^\]]+)\]\]/g, '')
    .replace(/\s{3,}/g, '\n\n')
    .trim();
}

// Sauvegarde dans localStorage
function saveChatState(messages, responseCount) {
  try {
    const serialized = messages.map(m => ({
      ...m,
      ts: m.ts instanceof Date ? m.ts.toISOString() : m.ts,
    }));
    localStorage.setItem(CHAT_STORAGE, JSON.stringify({ messages: serialized, responseCount }));
  } catch { /* localStorage plein ou désactivé */ }
}

// Restauration depuis localStorage
function loadChatState() {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE);
    if (!raw) return null;
    const { messages, responseCount } = JSON.parse(raw);
    const restored = (messages || []).map(m => ({
      ...m,
      ts: m.ts ? new Date(m.ts) : null,
    }));
    // Garder seulement les 50 derniers messages pour ne pas alourdir
    return { messages: restored.slice(-50), responseCount: responseCount || 0 };
  } catch {
    return null;
  }
}

// ── Markdown avec liens en nouvel onglet ──────────────────────────────────────
function ChatMarkdown({ content }) {
  const clean = cleanContent(content);
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Tous les liens s'ouvrent dans un nouvel onglet
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:text-primary/80"
              {...props}
            >
              {children}
            </a>
          ),
        }}
      >
        {clean}
      </ReactMarkdown>
    </div>
  );
}

// ── Bulle de message ──────────────────────────────────────────────────────────
function ChatBubble({ msg, wide }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
        ${isUser ? 'bg-primary/20 border border-primary/30' : 'bg-gradient-to-br from-primary to-secondary'}`}>
        {isUser ? <User className="w-4 h-4 text-primary" /> : <Bot className="w-4 h-4 text-white" />}
      </div>
      <div className={`rounded-2xl px-4 py-2.5 leading-relaxed
        ${wide ? 'max-w-[85%]' : 'max-w-[80%]'}
        ${isUser
          ? 'bg-primary/15 border border-primary/25 text-foreground rounded-tr-sm text-sm'
          : 'bg-card/60 border border-border/30 text-foreground/90 rounded-tl-sm'}`}>
        {isUser
          ? <p className="text-sm">{msg.content}</p>
          : <ChatMarkdown content={msg.content} />}
        {msg.ts && (
          <div className={`text-[10px] mt-1.5 ${isUser ? 'text-primary/50 text-right' : 'text-muted-foreground'}`}>
            {(msg.ts instanceof Date ? msg.ts : new Date(msg.ts)).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
        <Bot className="w-4 h-4 text-white" />
      </div>
      <div className="bg-card/60 border border-border/30 rounded-2xl rounded-tl-sm px-4 py-3">
        <div className="flex gap-1 items-center h-4">
          {[0, 150, 300].map(d => (
            <span key={d} className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: `${d}ms` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
function ChatHeader({ mode, onToggleMode, onClose, onLoginClick, remaining }) {
  const { user, logout, isAuthenticated } = useAuth();
  return (
    <div className="flex items-center gap-2 px-4 py-3 flex-shrink-0
      bg-gradient-to-r from-primary/20 to-secondary/10 border-b border-border/30">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
        {isAuthenticated && user?.picture
          ? <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
          : <Bot className="w-5 h-5 text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground truncate">
          {isAuthenticated ? user?.name || 'Expert Auth0 IA' : 'Expert Auth0 IA'}
        </p>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          {isAuthenticated ? (
            <span className="text-xs text-green-400">Connecté · messages illimités</span>
          ) : remaining >= 0 ? (
            <span className="text-xs text-amber-400">{remaining} message{remaining !== 1 ? 's' : ''} gratuit{remaining !== 1 ? 's' : ''} restant{remaining !== 1 ? 's' : ''}</span>
          ) : (
            <span className="text-xs text-muted-foreground">En ligne · {FREE_LIMIT} messages gratuits</span>
          )}
        </div>
      </div>

      {/* Bouton login/logout */}
      {isAuthenticated ? (
        <button onClick={logout} title="Se déconnecter"
          className="text-muted-foreground hover:text-primary transition-colors p-1.5 rounded-lg hover:bg-primary/10">
          <LogOut className="w-4 h-4" />
        </button>
      ) : (
        <button onClick={onLoginClick} title="Se connecter"
          className="text-muted-foreground hover:text-primary transition-colors p-1.5 rounded-lg hover:bg-primary/10">
          <LogIn className="w-4 h-4" />
        </button>
      )}

      <button onClick={onToggleMode}
        title={mode === 'sidebar' ? 'Mode popup' : 'Ouvrir en panneau latéral'}
        className="text-muted-foreground hover:text-primary transition-colors p-1.5 rounded-lg hover:bg-primary/10">
        {mode === 'sidebar' ? <Minimize2 className="w-4 h-4" /> : <PanelRight className="w-4 h-4" />}
      </button>
      <button onClick={onClose}
        className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-primary/10">
        {mode === 'sidebar' ? <X className="w-4 h-4" /> : <ChevronDown className="w-5 h-5" />}
      </button>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function ExpertChat() {
  const { isAuthenticated, token } = useAuth();

  // Restauration depuis localStorage
  const savedState = loadChatState();

  const [open, setOpen]               = useState(false);
  const [mode, setMode]               = useState('popup');
  const [messages, setMessages]       = useState(savedState?.messages || [WELCOME_MSG]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [unread, setUnread]           = useState(0);
  const [sidebarW, setSidebarW]       = useState(SIDEBAR_DEFAULT);
  const [showLogin, setShowLogin]     = useState(false);
  const [loginReason, setLoginReason] = useState('login');
  const [remaining, setRemaining]     = useState(
    isAuthenticated ? -1 : (savedState ? FREE_LIMIT - (savedState.responseCount || 0) : FREE_LIMIT)
  );

  const endRef      = useRef(null);
  const inputRef    = useRef(null);
  const resizing    = useRef(false);
  const resizeStart = useRef({ x: 0, w: 0 });

  const apiEndpoint = COMPANY.chatApiEndpoint || `${process.env.REACT_APP_API_URL || ''}/api/chat`;

  // Sauvegarde auto quand les messages changent
  const responseCount = isAuthenticated ? 0 : Math.max(0, FREE_LIMIT - remaining);
  useEffect(() => {
    if (messages.length > 0) {
      saveChatState(messages, responseCount);
    }
  }, [messages, responseCount]);

  // Mettre à jour remaining quand l'utilisateur se connecte
  useEffect(() => {
    if (isAuthenticated) setRemaining(-1);
  }, [isAuthenticated]);

  // Auto-scroll
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  // Focus input
  useEffect(() => {
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 120); }
  }, [open, mode]);

  // Escape ferme sidebar
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && mode === 'sidebar') setMode('popup'); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [mode]);

  // ── Resize sidebar ──────────────────────────────────────────────────────────
  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    resizing.current = true;
    resizeStart.current = { x: e.clientX, w: sidebarW };
    const onMove = (ev) => {
      if (!resizing.current) return;
      const delta = resizeStart.current.x - ev.clientX;
      setSidebarW(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, resizeStart.current.w + delta)));
    };
    const onUp = () => {
      resizing.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [sidebarW]);

  // ── Envoi message ───────────────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { id: Date.now(), role: 'user', content: text, ts: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = messages
        .filter(m => m.id !== 'welcome')
        .map(m => ({ role: m.role, content: m.content }));

      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          system: SYSTEM_PROMPT,
          messages: [...history, { role: 'user', content: text }],
        }),
      });

      // Limite atteinte
      if (res.status === 429) {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: '🔒 Vous avez utilisé vos 10 messages gratuits. **Connectez-vous** pour continuer sans limite.',
          ts: new Date(),
        }]);
        setRemaining(0);
        setLoginReason('limit');
        setShowLogin(true);
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Mettre à jour le compteur de messages restants
      if (!isAuthenticated && typeof data.remaining === 'number') {
        setRemaining(data.remaining);
      }

      const reply = data.reply || data.content || data.message
        || data.choices?.[0]?.message?.content
        || 'Désolé, je n\'ai pas pu traiter votre demande.';

      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: reply, ts: new Date() }]);
      if (!open) setUnread(n => n + 1);

    } catch {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Une erreur s\'est produite. Contactez-nous directement : contact@auth0.expert',
        ts: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleClose = () => { setOpen(false); setMode('popup'); };
  const toggleMode  = () => setMode(m => m === 'popup' ? 'sidebar' : 'popup');
  const openLogin   = () => { setLoginReason('login'); setShowLogin(true); };

  // ── Corps partagé popup + sidebar ──────────────────────────────────────────
  const chatBody = (
    <>
      <div className="flex-1 overflow-y-auto chat-scroll p-4 space-y-4">
        {messages.map(msg => <ChatBubble key={msg.id} msg={msg} wide={mode === 'sidebar'} />)}
        {loading && <TypingIndicator />}
        <div ref={endRef} />
      </div>

      {/* Suggestions */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2 flex-shrink-0">
          {SUGGESTIONS.map(q => (
            <button key={q}
              onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
              className="text-xs px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Bannière limite proche (2 messages restants) */}
      {!isAuthenticated && remaining >= 0 && remaining <= 2 && remaining > 0 && (
        <div className="mx-3 mb-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20
          flex items-center justify-between gap-2 flex-shrink-0">
          <p className="text-xs text-amber-400">
            Plus que <strong>{remaining}</strong> message{remaining !== 1 ? 's' : ''} gratuit{remaining !== 1 ? 's' : ''}
          </p>
          <button onClick={openLogin}
            className="text-xs text-primary underline hover:no-underline flex-shrink-0">
            Se connecter
          </button>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-3 border-t border-border/30 flex gap-2 flex-shrink-0">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={mode === 'sidebar' ? 2 : 1}
          placeholder={remaining === 0 && !isAuthenticated
            ? 'Connectez-vous pour continuer...'
            : 'Posez votre question...'}
          disabled={remaining === 0 && !isAuthenticated}
          className="flex-1 resize-none bg-card/40 border border-border/40 rounded-xl px-3 py-2
            text-sm text-foreground placeholder:text-muted-foreground focus:outline-none
            focus:border-primary/50 transition-colors chat-scroll disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ maxHeight: mode === 'sidebar' ? '120px' : '100px' }}
        />
        {remaining === 0 && !isAuthenticated ? (
          <button onClick={openLogin}
            className="w-10 h-10 rounded-xl bg-primary hover:bg-primary/90 flex items-center justify-center
              flex-shrink-0 transition-all glow-purple self-end">
            <LogIn className="w-4 h-4 text-white" />
          </button>
        ) : (
          <button onClick={sendMessage} disabled={!input.trim() || loading}
            className="w-10 h-10 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40
              flex items-center justify-center flex-shrink-0 transition-all glow-purple self-end">
            {loading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* ══ SIDEBAR ═══════════════════════════════════════════════════════════ */}
      {open && mode === 'sidebar' && (
        <div className="fixed top-0 right-0 bottom-0 z-50 flex flex-col
          bg-background/96 backdrop-blur-xl border-l border-primary/30
          shadow-[-24px_0_60px_hsl(280_85%_58%/0.12)]"
          style={{ width: sidebarW }}>
          <div onMouseDown={onResizeStart}
            className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 group"
            title="Glisser pour redimensionner">
            <div className="absolute inset-y-0 left-0 w-1.5 bg-primary/0 group-hover:bg-primary/40 transition-colors rounded-r" />
            <div className="absolute top-1/2 left-0 -translate-y-1/2 flex flex-col gap-1 pl-px">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-0.5 h-4 bg-primary/25 group-hover:bg-primary/60 rounded-full transition-colors" />
              ))}
            </div>
          </div>
          <ChatHeader mode="sidebar" onToggleMode={toggleMode} onClose={handleClose}
            onLoginClick={openLogin} remaining={remaining} />
          {chatBody}
        </div>
      )}

      {/* ══ POPUP ═════════════════════════════════════════════════════════════ */}
      {open && mode === 'popup' && (
        <div className="fixed bottom-24 right-4 md:right-6 z-50 w-[min(400px,calc(100vw-2rem))] flex flex-col
          glass border border-primary/30 rounded-2xl shadow-[0_0_60px_hsl(280_85%_58%/0.2)] overflow-hidden"
          style={{ height: 'min(560px, calc(100dvh - 120px))' }}>
          <ChatHeader mode="popup" onToggleMode={toggleMode} onClose={handleClose}
            onLoginClick={openLogin} remaining={remaining} />
          {chatBody}
        </div>
      )}

      {/* ══ BOUTON FLOTTANT ═══════════════════════════════════════════════════ */}
      {!(open && mode === 'sidebar') && (
        <button onClick={() => setOpen(o => !o)}
          className="fixed bottom-6 right-4 md:right-6 z-50 w-14 h-14 rounded-full
            bg-gradient-to-br from-primary to-secondary shadow-[0_0_30px_hsl(280_85%_58%/0.4)]
            flex items-center justify-center hover:scale-110 transition-transform"
          aria-label="Chat expert Auth0">
          {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
          {!open && unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 border-2 border-background
              text-white text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* ══ LOGIN MODAL ═══════════════════════════════════════════════════════ */}
      {showLogin && (
        <LoginModal
          reason={loginReason}
          onClose={() => setShowLogin(false)}
        />
      )}
    </>
  );
}
